import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "clsx";

import {getColorForNumber} from "../../../utils/helpers";

import Progress from "../../../components/CircleProgressbar/CircleProgressbar";

const StatisticResults = ({ statisticData, preNumbersUOText, firstNumberUOText }) => {
  const { t } = useTranslation();
  const [statisticForRender, setStatisticForRender] = useState({
    mostTimes: [],
    leastTimes: [],
    firstNumberEvenOdd: [],
    firstNumberUnderOver: [],
    preNumbersOddEven: [],
    preNumbersSumUnderOver: [],
    firstBallMostColor: []
  });

  const setStatisticDataForRender = () => {

    // get most | least frequently numbers with its colors property for each number
    function getMostLeastNumbers(mostTimesNumbers) {
      return mostTimesNumbers.map(item => {
        const itemCopy = { ...item };
        itemCopy.color = getColorForNumber(itemCopy.Number);
        return itemCopy;
      });
    }
    // get the values for circle diagram for each bet type
    function getStatsByBetType(betTypeStats, betType) {
      return betTypeStats.filter(item => {
        return Number(item.BetType) === Number(betType);
      });
    }
    // get most frequent color
    function getFirstBallMostColors(firstBallColor) {
      return firstBallColor.map(item => {
        const itemCopy = { ...item };
        itemCopy.color = getColorForNumber(itemCopy.Number);
        return itemCopy;
      });
    }

    // prepare object for statistic block
    const preparedStatistics = {
      mostTimes: getMostLeastNumbers([...statisticData.MostTimes ? statisticData.MostTimes : []]),
      leastTimes: getMostLeastNumbers([...statisticData.LeastTimes ? statisticData.LeastTimes : []]),
      firstNumberEvenOdd: getStatsByBetType([...statisticData.BetTypeStats ? statisticData.BetTypeStats : []], 2),
      firstNumberUnderOver: getStatsByBetType(
        [...statisticData.BetTypeStats ? statisticData.BetTypeStats : []],
        3
      ),
      preNumbersOddEven: getStatsByBetType([...statisticData.BetTypeStats ? statisticData.BetTypeStats : []], 4),
      preNumbersSumUnderOver: getStatsByBetType(
        [...statisticData.BetTypeStats ? statisticData.BetTypeStats : []],
        5
      ),
      firstBallMostColor: getFirstBallMostColors([
        ...statisticData.FirstBallColor ? statisticData.FirstBallColor : []
      ])
    };
    setStatisticForRender({ ...preparedStatistics });
  };

  // refresh the statistic
  useEffect(() => {
    if (Object.keys(statisticData).length > 0) setStatisticDataForRender();
    // eslint-disable-next-line
  }, [statisticData]);

  return Object.keys(statisticData).length === 0 || (
    !statisticData.MostTimes && 
    !statisticData.LeastTimes && 
    !statisticData.BetTypeStats && 
    !statisticData.FirstBallColor
  ) ? (
    t("statistics.emptyMessage")
  ) : (
    <>
      {/*Drawn most times*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-11 font-light mb-5">
          {t("statistics.drawnMostTimes")}
        </div>
      </div>
      <div className="statistics-balls-container flex-row justify-around">
        {statisticForRender.mostTimes.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
        {statisticForRender.mostTimes.map(ball => {
          return (
            <div className={"align-center"} key={`${ball.Number}${ball.Value}`}>
              <div
                className={cn(`spaceholder-balls flex-row`, {
                  [`${ball.color}`]: true
                })}
              >
                <span className="ball-number">{ball.Number}</span>
              </div>
              <span className="ball-label">{ball.Value}</span>
            </div>
          );
        })}
      </div>
      <div className="bet-content-divider"></div>

      {/*Drawn least times*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-11 font-light mb-5">
          {t("statistics.drawnLeastTimes")}
        </div>
      </div>
      <div className="statistics-balls-container flex-row justify-around">
        {statisticForRender.leastTimes.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
        {statisticForRender.leastTimes.map(ball => {
          return (
            <div className={"align-center"} key={`${ball.Number}${ball.Value}`}>
              <div
                className={cn(`spaceholder-balls flex-row`, {
                  [`${ball.color}`]: true
                })}
              >
                <span className="ball-number">{ball.Number}</span>
              </div>
              <span className="ball-label">{ball.Value}</span>
            </div>
          );
        })}
      </div>
      <div className="bet-content-divider"></div>

      {/*First number even/odd*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-21 font-light ">
          {t("statistics.firstNumberEvenOdd")}
          <div className="statistics-odds-container flex-row">
            {statisticForRender.firstNumberEvenOdd.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
            {statisticForRender.firstNumberEvenOdd.map(item => {
              return (
                <Progress
                  key={`${item.Value}_${item.Specifier}_${item.BetType}`}
                  strokeWidth={5}
                  label={Number(item.Specifier) === 1 ? "EVEN" : "ODD"}
                  percentage={item.Value}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="bet-content-divider"></div>

      {/*First number -24.5+*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-21 font-light ">
          {t("statistics.firstNumber")} {firstNumberUOText}
          <div className="statistics-odds-container flex-row">
            {statisticForRender.firstNumberUnderOver.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
            {statisticForRender.firstNumberUnderOver.map(item => {
              return (
                <Progress
                  key={`${item.Value}_${item.Specifier}_${item.BetType}`}
                  strokeWidth={5}
                  label={Number(item.Specifier) === 1 ? "UNDER" : "OVER"}
                  percentage={item.Value}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="bet-content-divider"></div>

      {/*Odd/Even Pre Numbers*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-21 font-light ">
          {t("statistics.preNumbersOddEven")}
          <div className="statistics-odds-container flex-row">
            {statisticForRender.preNumbersOddEven.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
            {statisticForRender.preNumbersOddEven.map(item => {
              return (
                <Progress
                  key={`${item.Value}_${item.Specifier}_${item.BetType}`}
                  strokeWidth={5}
                  label={Number(item.Specifier) === 1 ? "EVEN" : "ODD"}
                  percentage={item.Value}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="bet-content-divider"></div>

      {/*Pre Numbers sum -122.5+*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-21 font-light ">
          {t("statistics.preNumbersOddEven")} {preNumbersUOText}
          <div className="statistics-odds-container flex-row">
            {statisticForRender.preNumbersSumUnderOver.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
            {statisticForRender.preNumbersSumUnderOver.map(item => {
              return (
                <Progress
                  key={`${item.Value}_${item.Specifier}_${item.BetType}`}
                  strokeWidth={5}
                  label={Number(item.Specifier) === 1 ? "UNDER" : "OVER"}
                  percentage={item.Value}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="bet-content-divider"></div>

      {/*First ball color*/}
      <div className="flex-row bet-content-row">
        <div className="flex-col-11 font-light mb-5">{t("statistics.firstBallColor")}</div>
      </div>
      <div className="d-flex justify-around">
        {statisticForRender.firstBallMostColor.length === 0 && (<div className="empty-stat">There are no statistics yet</div>)}
        {statisticForRender.firstBallMostColor.map(item => {
          return (
            <div
              className={"align-center"}
              key={`${item.Number}_${item.Value}`}
            >
              <div className={cn(`ball-color alias-${item.color}`, {})}></div>
              <span className="ball-label">{item.Value}</span>
            </div>
          );
        })}
      </div>
      <div className="bet-content-divider"></div>
    </>
  );
};

export default StatisticResults;
